import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { blogsAPI } from '../../lib/supabase';
import './BlogDetail.css';

const BlogDetail = () => {
  const { slug } = useParams();
  const [blogData, setBlogData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        // Fetch all blogs from Supabase
        const blogs = await blogsAPI.getAll();
        let adminBlogs = {};
        
        // Convert Supabase blogs to the format expected by BlogDetail
        blogs.forEach(blog => {
          const blogSlug = blog.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
          adminBlogs[blogSlug] = {
            title: blog.title,
            excerpt: blog.description,
            image: blog.image || 'https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            publishDate: blog.publish_date,
            content: blog.content || '<p>Content not available.</p>'
          };
        });

        // Combine with default blog data
        const allBlogData = { ...defaultBlogData, ...adminBlogs };
        
        // Set all blog data for lookup
        setBlogData(allBlogData);
        
        setLoading(false);
        console.log('Blog data loaded from Supabase for slug:', slug);
      } catch (error) {
        console.error('Error fetching blog data from Supabase:', error);
        // Fallback to default data only
        setBlogData(defaultBlogData);
        setLoading(false);
      }
    };

    // Default solar energy blog data that matches Blog.js
    const defaultBlogData = {
      'future-solar-energy-technologies-2024': {
        title: 'The Future of Solar Energy: Emerging Technologies in 2024',
        excerpt: 'Explore cutting-edge solar technologies including perovskite cells, floating solar farms, and AI-powered energy management systems.',
        image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        publishDate: '2024-01-20',
        content: `
          <div class="blog-content">
            <h2>Revolutionary Solar Technologies Shaping 2024</h2>
            <p>The solar energy industry is experiencing unprecedented innovation in 2024, with breakthrough technologies promising to revolutionize how we harness and utilize solar power. From next-generation photovoltaic cells to intelligent energy management systems, these emerging technologies are setting new standards for efficiency, affordability, and accessibility.</p>
            
            <h3>Perovskite Solar Cells: The Game Changer</h3>
            <p>Perovskite solar cells have emerged as one of the most promising technologies in the solar industry. These cells offer several advantages over traditional silicon-based panels:</p>
            <ul>
              <li><strong>Higher Efficiency:</strong> Laboratory tests have achieved efficiency rates exceeding 25%, with potential for even higher performance</li>
              <li><strong>Lower Manufacturing Costs:</strong> Perovskite cells can be produced using simpler, less energy-intensive processes</li>
              <li><strong>Flexibility:</strong> These cells can be manufactured on flexible substrates, opening new applications</li>
              <li><strong>Lightweight Design:</strong> Significantly lighter than traditional panels, making installation easier</li>
            </ul>
            
            <h3>Floating Solar Farms: Maximizing Space Utilization</h3>
            <p>Floating photovoltaic (FPV) systems are gaining traction worldwide, offering unique benefits:</p>
            <ul>
              <li><strong>Land Conservation:</strong> Utilizes water bodies without consuming valuable land resources</li>
              <li><strong>Improved Efficiency:</strong> Water cooling effect enhances panel performance by 10-15%</li>
              <li><strong>Reduced Evaporation:</strong> Panels help reduce water evaporation from reservoirs</li>
              <li><strong>Algae Control:</strong> Shading effect helps control algae growth in water bodies</li>
            </ul>
            
            <h3>AI-Powered Energy Management</h3>
            <p>Artificial Intelligence is transforming solar energy management through:</p>
            <ul>
              <li><strong>Predictive Analytics:</strong> AI algorithms predict energy production based on weather patterns</li>
              <li><strong>Smart Grid Integration:</strong> Optimizes energy distribution and storage</li>
              <li><strong>Maintenance Optimization:</strong> Predictive maintenance reduces downtime and costs</li>
              <li><strong>Performance Monitoring:</strong> Real-time analysis identifies and resolves issues quickly</li>
            </ul>
            
            <h3>Bifacial Solar Panels</h3>
            <p>Bifacial panels capture sunlight from both sides, increasing energy generation by 15-30% compared to traditional monofacial panels. These panels are particularly effective in environments with high ground reflectance, such as snow-covered areas or light-colored surfaces.</p>
            
            <h3>Solar Paint and Building-Integrated Photovoltaics</h3>
            <p>Innovative approaches to solar integration include:</p>
            <ul>
              <li><strong>Photovoltaic Paint:</strong> Solar cells integrated into paint for building surfaces</li>
              <li><strong>Solar Windows:</strong> Transparent solar cells integrated into building windows</li>
              <li><strong>Solar Roof Tiles:</strong> Aesthetically pleasing alternatives to traditional panels</li>
            </ul>
            
            <h3>The Indian Context</h3>
            <p>India is at the forefront of adopting these emerging technologies. The government's ambitious renewable energy targets and supportive policies are driving innovation and adoption. Key developments include:</p>
            <ul>
              <li>Large-scale floating solar projects in states like Gujarat and Rajasthan</li>
              <li>Integration of AI in grid management systems</li>
              <li>Research partnerships with international technology providers</li>
              <li>Incentives for adopting next-generation solar technologies</li>
            </ul>
            
            <h3>Future Outlook</h3>
            <p>The convergence of these technologies promises a future where solar energy becomes even more efficient, affordable, and accessible. As manufacturing scales up and costs decrease, we can expect widespread adoption of these innovations, bringing us closer to a sustainable energy future.</p>
            
            <p>For businesses and homeowners in Gujarat and across India, staying informed about these emerging technologies is crucial for making informed decisions about solar investments. The solar industry's rapid evolution means that today's cutting-edge technology will become tomorrow's standard, making now an exciting time to embrace solar energy.</p>
          </div>
        `
      },
      'residential-solar-installation-gujarat-guide': {
        title: 'Complete Guide to Residential Solar Installation in Gujarat',
        excerpt: 'Everything you need to know about installing solar panels on your home in Gujarat, including costs, permits, and government subsidies.',
        image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        publishDate: '2024-01-18',
        content: `
          <div class="blog-content">
            <h2>Your Complete Guide to Residential Solar Installation in Gujarat</h2>
            <p>Gujarat has emerged as India's solar energy leader, offering excellent conditions for residential solar installations. This comprehensive guide covers everything you need to know about installing solar panels on your home in Gujarat.</p>
            
            <h3>Why Gujarat is Perfect for Solar Energy</h3>
            <p>Gujarat offers ideal conditions for solar energy generation:</p>
            <ul>
              <li><strong>Abundant Sunshine:</strong> 300+ sunny days per year with high solar irradiation</li>
              <li><strong>Government Support:</strong> Progressive policies and attractive subsidies</li>
              <li><strong>Grid Infrastructure:</strong> Well-developed electrical grid for net metering</li>
              <li><strong>Industrial Ecosystem:</strong> Strong solar manufacturing and installation network</li>
            </ul>
            
            <h3>Understanding Solar System Types</h3>
            <h4>Grid-Tied Systems</h4>
            <ul>
              <li>Connected to the utility grid</li>
              <li>Excess energy fed back to grid (net metering)</li>
              <li>Lower initial cost</li>
              <li>No backup during power outages</li>
            </ul>
            
            <h4>Off-Grid Systems</h4>
            <ul>
              <li>Independent of utility grid</li>
              <li>Includes battery storage</li>
              <li>Higher initial cost</li>
              <li>Complete energy independence</li>
            </ul>
            
            <h4>Hybrid Systems</h4>
            <ul>
              <li>Best of both worlds</li>
              <li>Grid connection with battery backup</li>
              <li>Highest initial cost</li>
              <li>Maximum flexibility and reliability</li>
            </ul>
            
            <h3>Cost Analysis for Gujarat Homes</h3>
            <p>Typical costs for residential solar installations in Gujarat:</p>
            <ul>
              <li><strong>1 kW System:</strong> ₹60,000 - ₹80,000</li>
              <li><strong>3 kW System:</strong> ₹1,80,000 - ₹2,40,000</li>
              <li><strong>5 kW System:</strong> ₹3,00,000 - ₹4,00,000</li>
              <li><strong>10 kW System:</strong> ₹6,00,000 - ₹8,00,000</li>
            </ul>
            
            <p>Installing solar panels on your Gujarat home is not just an environmental choice—it's a smart financial decision. With abundant sunshine, supportive policies, and decreasing costs, there has never been a better time to go solar in Gujarat.</p>
          </div>
        `
      },
      'solar-energy-roi-savings-india': {
        title: 'Solar Energy ROI: How Much Can You Save in India?',
        excerpt: 'Discover the real financial benefits of solar energy in India with detailed ROI calculations and payback period analysis.',
        image: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        publishDate: '2024-01-15',
        content: `
          <div class="blog-content">
            <h2>Understanding Solar Energy ROI in India</h2>
            <p>Solar energy has become one of the most attractive investment options for Indian homeowners and businesses. With declining panel costs, government incentives, and rising electricity tariffs, the return on investment (ROI) for solar installations has never been better.</p>
            
            <h3>How to Calculate Solar ROI</h3>
            <p>Solar ROI calculation involves several factors:</p>
            <ul>
              <li><strong>Initial Investment:</strong> Total cost of solar system including installation</li>
              <li><strong>Annual Savings:</strong> Electricity bill reduction per year</li>
              <li><strong>Government Incentives:</strong> Subsidies and tax benefits</li>
              <li><strong>System Lifespan:</strong> Typically 25 years for solar panels</li>
            </ul>
            
            <h3>Payback Period Analysis</h3>
            <p>The payback period for solar installations in India varies by state and system size:</p>
            <ul>
              <li><strong>Residential Systems:</strong> 4-7 years</li>
              <li><strong>Commercial Systems:</strong> 3-5 years</li>
              <li><strong>Industrial Systems:</strong> 2-4 years</li>
            </ul>
            
            <h3>Long-term Financial Benefits</h3>
            <p>Over a 25-year period, solar installations can provide substantial savings:</p>
            <ul>
              <li>Total savings of ₹15-30 lakhs for average homes</li>
              <li>Protection against rising electricity costs</li>
              <li>Increased property value</li>
              <li>Potential income from excess energy sales</li>
            </ul>
            
            <p>Solar energy represents not just an environmental choice, but a smart financial decision that pays dividends for decades to come.</p>
          </div>
        `
      }
    };

    // Call the async function
    fetchBlogData();
  }, [slug]);

  const post = blogData[slug];

  // Debug logging
  console.log('Current slug:', slug);
  console.log('Available blog data keys:', Object.keys(blogData));
  console.log('Found post:', post);

  if (loading) {
    return (
      <div className="blog-loading">
        <div className="container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="blog-not-found">
        <div className="container">
          <h1>Article Not Found</h1>
          <p>The article you're looking for doesn't exist or has been removed.</p>
          <Link to="/blog" className="btn btn-primary">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <>
      <Helmet>
        <title>{post.title} - Global Solar Energy Blog</title>
        <meta name="description" content={post.excerpt} />
      </Helmet>

      <div className="blog-detail-page">
        <div className="container">
          <div className="blog-detail-content">
            {/* Back to Blog Link */}
            <div className="back-link">
              <Link to="/blog" className="back-btn">
                <i className="fas fa-arrow-left"></i> Back to Blog
              </Link>
            </div>

            {/* Blog Header Section with Image and All Info */}
            <div className="blog-header-section">
              {/* Featured Image */}
              <div className="blog-image">
                <img src={post.image} alt={post.title} />
              </div>
              
              {/* Blog Info */}
              <div className="blog-info">
                {/* Blog Title */}
                <h1 className="blog-title">{post.title}</h1>

                {/* Blog Description */}
                <p className="blog-description">{post.excerpt}</p>

                {/* Publish Date */}
                <div className="blog-date">
                  <i className="fas fa-calendar-alt"></i>
                  <span>Published on {formatDate(post.publishDate)}</span>
                </div>
              </div>
            </div>

            {/* Blog Content */}
            <div className="blog-content-wrapper">
              <div 
                className="blog-content"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogDetail;